import { create } from 'zustand';

import {
  clearBulkProcessQueue,
  loadBulkProcessQueue,
  mergeBulkTargetIds,
  saveBulkProcessQueue,
} from '@/lib/bulkProcessQueuePersistence';
import { countBulkBannerCompleted } from '@/lib/bulkProcessQueueSync';
import type { Application } from '@/types';

export type StartBulkRunOptions = {
  /** Append to an in-flight batch instead of replacing banner scope. */
  merge?: boolean;
  /** Restore from sessionStorage — do not reset completed counter. */
  resume?: boolean;
  /** Restored completed count (from sessionStorage on F5). */
  initialCompleted?: number;
};

interface BulkProcessBannerState {
  companyId: string | null;
  targetIds: string[];
  completed: number;
  phase: 'idle' | 'active';
  startBulkRun: (
    companyId: string,
    applicationIds: string[],
    options?: StartBulkRunOptions,
  ) => void;
  syncProgress: (
    companyId: string | undefined,
    processingIds: Set<string>,
    stoppingIds: Set<string>,
    applications?: Application[],
  ) => void;
  clearBulkRun: () => void;
}

const initialState = {
  companyId: null as string | null,
  targetIds: [] as string[],
  completed: 0,
  phase: 'idle' as const,
};

export const useBulkProcessBannerStore = create<BulkProcessBannerState>((set, get) => ({
  ...initialState,
  startBulkRun: (companyId, applicationIds, options) => {
    if (applicationIds.length === 0) return;
    const state = get();
    const merging =
      options?.merge === true
      && state.phase === 'active'
      && state.companyId === companyId;

    if (merging) {
      set({
        targetIds: mergeBulkTargetIds(state.targetIds, applicationIds),
      });
      return;
    }

    set({
      companyId,
      targetIds: applicationIds,
      completed: options?.resume
        ? Math.max(0, options.initialCompleted ?? get().completed)
        : 0,
      phase: 'active',
    });
  },
  syncProgress: (companyId, processingIds, stoppingIds, applications) => {
    const state = get();
    if (state.phase !== 'active' || !state.companyId || state.companyId !== companyId) return;
    if (state.targetIds.length === 0) return;

    const bulkScope = new Set(state.targetIds);
    const completed = countBulkBannerCompleted(
      state.targetIds,
      applications ?? [],
      processingIds,
      stoppingIds,
      bulkScope,
    );
    const nextCompleted = Math.min(
      Math.max(completed, state.completed),
      state.targetIds.length,
    );
    // Dismiss when every batch row reached a settled server state (queue ids may lag one poll).
    if (nextCompleted >= state.targetIds.length) {
      clearBulkProcessQueue(state.companyId);
      set({ ...initialState });
      return;
    }
    set({ completed: nextCompleted });
    const persisted = loadBulkProcessQueue(state.companyId);
    saveBulkProcessQueue(
      state.companyId,
      stoppingIds,
      processingIds,
      state.targetIds,
      nextCompleted,
      persisted.targetRefs,
    );
  },
  clearBulkRun: () => set({ ...initialState }),
}));

export function bulkBannerTotal(state: Pick<BulkProcessBannerState, 'targetIds'>): number {
  return state.targetIds.length;
}

export { mergeBulkTargetIds };
